/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../logger';
import { reportRuleFailure } from '../rules/debug';
import { createFactLayer } from '../rules/facts/resolver';
import { FactScope, internConditions, evaluateCompiled, shareKeyFor } from '../rules/ruleset';
import { clone } from '../util';
import { ActivityManager } from '../activity/manager';

import type { CompiledRuleSet } from '../rules/ruleset';
import type { FailedCondition, Facts } from '../rules/index';
import type { AdData, KeyValue } from '../types';
import type { HierarchyNode } from '../activity/manager';

const logger = Logger.getInstance();

class Ad {
  private data: AdData;
  private ruleSet: CompiledRuleSet | null = null;
  // ad.* activity facts plus caller-added facts; layered over the campaign facts
  private ownFacts: Facts = {};
  private evalFacts: Facts | null = null;
  private evalParent: Facts | null = null;
  private eligibilityResult: boolean | null = null;
  private factsDirty: boolean = true;
  private hasCallerFacts: boolean = false;
  private _keyValues: KeyValue[] = [];
  private _selectionLabel: string | undefined = undefined;

  constructor(adData: AdData) {
    this.data = adData;
  }

  public get deliveryId(): string {
    return this.data.id;
  }

  public set keyValues(keyValues: KeyValue[]) {
    this._keyValues = clone(keyValues);
  }

  /**
   * Returns the linked line ID with backwards compatibility.
   * Priority: elid > geid > null
   */
  public get linkedLineId(): string | null {
    return this.data.elid ?? this.data.geid ?? null;
  }

  /**
   * Returns the linked creative ID.
   * Returns eid if available, otherwise falls back to geid if elid is not set.
   */
  public get linkedCreativeId(): string | null {
    // If we have eid, use it
    if (this.data.eid) {
      return this.data.eid;
    }
    // If we have geid but no elid, then geid might be the creative ID
    if (this.data.geid && !this.data.elid) {
      return this.data.geid;
    }
    return null;
  }

  public hasKeyValues(): boolean {
    return this._keyValues.length > 0;
  }

  public get keyValues(): KeyValue[] {
    return clone(this._keyValues);
  }

  public get selectionLabel(): string | undefined {
    return this._selectionLabel;
  }

  public set selectionLabel(label: string | undefined) {
    this._selectionLabel = label;
  }

  /**
   * Evaluates this ad's own rules against the layered facts, memoised until
   * the parent layer's identity changes (a new epoch or an upstream fact
   * change) or this ad's own facts are dirtied. Only ad-level conditions run
   * here: campaign and property conditions gate at their own scope.
   */
  public isEligible(parentFacts: Facts): boolean {
    if (!this.data?.rules?.conditions) {
      logger.warn(`Ad #${this.deliveryId} is missing conditions.`);
      return false;
    }

    this.ruleSet ??= internConditions(this.data.rules.conditions);

    // Ads that would ask this set the same question reuse one evaluation and
    // skip fact assembly. dbg ads always evaluate so their collectors run;
    // caller-added facts could shadow a referenced fact, so they never share.
    const shareKey =
      this.data.dbg === true || this.hasCallerFacts
        ? null
        : shareKeyFor(this.ruleSet, FactScope.AD, parentFacts);
    if (shareKey !== null && this.ruleSet.shareKey === shareKey) {
      return this.ruleSet.sharedResult!;
    }

    if (this.evalParent !== parentFacts || this.factsDirty) {
      this.setActivityFacts();
      this.evalFacts = createFactLayer(parentFacts, this.ownFacts);
      this.evalParent = parentFacts;
      this.factsDirty = false;
      this.eligibilityResult = null;
    }
    if (this.eligibilityResult !== null) {
      return this.eligibilityResult;
    }

    const collector = this.data.dbg === true ? ([] as FailedCondition[]) : undefined;
    const conditionsSatisfied = evaluateCompiled(this.ruleSet, this.evalFacts!, collector);
    this.eligibilityResult = conditionsSatisfied;
    if (shareKey !== null) {
      this.ruleSet.shareKey = shareKey;
      this.ruleSet.sharedResult = conditionsSatisfied;
    }

    if (collector !== undefined && !conditionsSatisfied && collector.length > 0) {
      reportRuleFailure(this.deliveryId, this.evalFacts!, collector, 'a');
    }

    return conditionsSatisfied;
  }

  public addFacts(facts: Facts): void {
    Object.assign(this.ownFacts, facts);
    this.factsDirty = true;
    this.hasCallerFacts = true;
  }

  private setActivityFacts(): void {
    const hierarchy: HierarchyNode = {
      id: this.deliveryId,
      children: [],
    };

    const activityManager = ActivityManager.getInstance();

    // Historical facts (30-day)
    this.ownFacts['ad.imps'] = activityManager.getImpsForEntity(this.deliveryId, hierarchy);
    this.ownFacts['ad.views'] = activityManager.getViewsForEntity(this.deliveryId, hierarchy);
    this.ownFacts['ad.ints'] = activityManager.getInteractionsForEntity(this.deliveryId, hierarchy);

    // Page view facts (current page only)
    this.ownFacts['ad.page.imps'] = activityManager.getImpsForEntity(
      this.deliveryId,
      hierarchy,
      true,
    ).length;
    this.ownFacts['ad.page.views'] = activityManager.getViewsForEntity(
      this.deliveryId,
      hierarchy,
      true,
    ).length;
    this.ownFacts['ad.page.ints'] = activityManager.getInteractionsForEntity(
      this.deliveryId,
      hierarchy,
      true,
    ).length;
  }
}

export { Ad };
